import { PDFDocument } from "pdf-lib"
import { useCallback, useState } from "react"

export interface SignaturePlacement {
  pageIndex: number
  x: number
  y: number
  width: number
  height: number
}

export function useSignPdf() {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const signPdf = useCallback(
    async (
      file: File,
      signatureBytes: Uint8Array,
      placements: SignaturePlacement[],
    ) => {
      setProcessing(true)
      setError(null)

      try {
        const arrayBuffer = await file.arrayBuffer()
        const pdfDoc = await PDFDocument.load(arrayBuffer, {
          ignoreEncryption: true,
        })

        // Detect image type from magic bytes
        const isPng =
          signatureBytes[0] === 0x89 &&
          signatureBytes[1] === 0x50 &&
          signatureBytes[2] === 0x4e &&
          signatureBytes[3] === 0x47

        const image = isPng
          ? await pdfDoc.embedPng(signatureBytes)
          : await pdfDoc.embedJpg(signatureBytes)

        for (const placement of placements) {
          const page = pdfDoc.getPage(placement.pageIndex)
          const { width: pageWidth, height: pageHeight } = page.getSize()

          const sigWidth = placement.width * pageWidth
          const sigHeight = placement.height * pageHeight
          const sigX = placement.x * pageWidth
          const sigY = (1 - placement.y) * pageHeight - sigHeight

          page.drawImage(image, {
            x: sigX,
            y: sigY,
            width: sigWidth,
            height: sigHeight,
          })
        }

        const pdfBytes = await pdfDoc.save()
        return new Blob([pdfBytes as BlobPart], { type: "application/pdf" })
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to sign PDF. The file may be corrupted or password-protected."
        setError(message)
        throw err
      } finally {
        setProcessing(false)
      }
    },
    [],
  )

  return { signPdf, processing, error }
}
